import { useEffect, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageUploader } from "./ImageUploader";
import { DefaultImagePicker, type DefaultKind } from "./DefaultImagePicker";
import { resolveAssetUrl } from "@/lib/assetUrl";
import { adminApi } from "@/lib/adminApi";

interface ImageOrDefaultUploaderProps {
  /** Either an absolute URL (uploaded) or a relative path ("bg/..." etc.). */
  value: string;
  /** Called with the new value (absolute URL or relative path). */
  onChange: (url: string) => void;
  /** Which default kinds to surface via the picker. */
  kinds: DefaultKind[];
  label?: string;
  maxSizeMB?: number;
  accept?: string;
  disabled?: boolean;
}

function isRelativePath(v: string): boolean {
  return /^(bg|fpv_flower|fpv_ring|fpv_dinner)\//.test(v);
}

/**
 * Wraps <ImageUploader> with a "Browse defaults" affordance for picking from
 * the local bg/, fpv_flower/, fpv_ring/ folders served by the backend.
 *
 * - Uploaded images produce absolute URLs (R2).
 * - Default picks produce relative paths (e.g. "bg/default_5.jpg") which the
 *   server resolves to absolute URLs when read back.
 *
 * Preview always uses resolveAssetUrl(value).
 */
export function ImageOrDefaultUploader({
  value,
  onChange,
  kinds,
  label = "image",
  maxSizeMB = 10,
  accept = "image/jpeg,image/png,image/webp,image/gif",
  disabled = false,
}: ImageOrDefaultUploaderProps) {
  // Track which kind picker is currently shown (one at a time).
  const [activeKind, setActiveKind] = useState<DefaultKind | null>(null);

  const previewUrl = value ? resolveAssetUrl(value) : "";
  const isDefault = isRelativePath(value);

  return (
    <div className="space-y-3">
      {previewUrl ? (
        <div className="relative">
          <img
            src={previewUrl}
            alt=""
            className="h-32 w-full rounded-lg border border-zinc-200 object-cover dark:border-zinc-700"
          />
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80"
              aria-label="Remove image"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="absolute bottom-2 left-2 rounded bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white">
            {isDefault ? "Default" : "Uploaded"}
          </div>
        </div>
      ) : (
        <div className="flex h-32 w-full items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
          <ImageIcon className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {/* Upload option (delegates to ImageUploader for state machine + preview) */}
        {!disabled && (
          <UploadOnlyButton
            label={label}
            value={value}
            onChange={onChange}
            maxSizeMB={maxSizeMB}
            accept={accept}
            disabled={disabled}
          />
        )}

        {/* Browse defaults — one button per kind */}
        {!disabled &&
          kinds.map((k) => (
            <DefaultImagePicker
              key={k}
              kind={k}
              value={value}
              onPick={(rel) => onChange(rel)}
              onClear={value ? () => onChange("") : undefined}
              disabled={disabled}
            />
          ))}

        {value && !disabled && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="rounded-lg px-2 py-1 text-xs text-zinc-500 hover:text-red-500"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Internal subcomponent: a bare upload trigger button (no preview) that defers
 * to the existing ImageUploader via a portal-less popover. For simplicity we
 * inline the same upload logic here so the parent can render it alongside the
 * "Browse defaults" buttons.
 */
function UploadOnlyButton({
  label,
  value,
  onChange,
  maxSizeMB,
  accept,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  maxSizeMB: number;
  accept: string;
  disabled?: boolean;
}) {
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "uploading" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxSizeMB * 1024 * 1024) {
      setErrorMsg(`File exceeds ${maxSizeMB} MB limit`);
      setStatus("error");
      return;
    }
    setPickedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setErrorMsg(null);
  };

  const upload = async () => {
    if (!pickedFile) return;
    setStatus("uploading");
    setErrorMsg(null);
    try {
      const res = await adminApi.uploadImage(pickedFile);
      onChange(res.url);
      setPickedFile(null);
      setPreviewUrl(null);
      setStatus("idle");
    } catch (err) {
      const e = err as { code?: string; message?: string };
      setErrorMsg(
        e.code === "FILE_TOO_LARGE"
          ? "File is too large."
          : e.code === "UNSUPPORTED_MEDIA_TYPE"
            ? "Format not supported."
            : e.code === "R2_NOT_CONFIGURED"
              ? "Storage not configured."
              : (e.message || "Upload failed"),
      );
      setStatus("error");
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onPick}
        disabled={disabled || status === "uploading"}
        className="hidden"
      />
      {!pickedFile && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          <Upload className="mr-2 h-3.5 w-3.5" />
          Upload {label.toLowerCase()}
        </Button>
      )}
      {pickedFile && (
        <div className="flex w-full items-center gap-2">
          {previewUrl && (
            <img
              src={previewUrl}
              alt=""
              className="h-10 w-10 rounded object-cover"
            />
          )}
          <span className="flex-1 truncate text-xs text-zinc-500 dark:text-zinc-400">
            {pickedFile.name} · {(pickedFile.size / 1024).toFixed(0)} KB
          </span>
          <Button type="button" size="sm" onClick={upload} disabled={status === "uploading"}>
            {status === "uploading" ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="mr-2 h-3.5 w-3.5" />
            )}
            Save
          </Button>
          <button
            type="button"
            onClick={() => {
              setPickedFile(null);
              setPreviewUrl(null);
              setStatus("idle");
              if (inputRef.current) inputRef.current.value = "";
            }}
            className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-red-500 dark:hover:bg-zinc-800"
            aria-label="Cancel"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {errorMsg && (
        <div className="w-full rounded-lg bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400">
          {errorMsg}
        </div>
      )}
    </>
  );
}